import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { aboutInfo } from '@/mocks/about';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9sm5o5lbrt1qcgaj620';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMsg('感谢您的留言，我们会尽快回复！');
      e.currentTarget.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快与您联系！');
        e.currentTarget.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMsg(errMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后再试');
    }
  };

  const orgSchema = {
    '@type': 'Organization',
    name: '蘑菇网',
    url: 'https://www.moguwang.com',
    description: '专注时尚女装的购物平台',
    foundingDate: '2018',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '南京西路1688号蘑菇大厦18层',
      addressLocality: '上海',
      addressRegion: '静安区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6688',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    sameAs: [
      'https://weibo.com/moguwang',
      'https://www.xiaohongshu.com/vendor/moguwang',
    ],
  };

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于蘑菇网 - 品牌故事与联系我们 | 蘑菇网官网"
        description="蘑菇网创立于2018年，专注时尚女装购物。汇聚500+品牌、海量潮流单品，为每一位女性提供个性化穿搭推荐与超值购物体验。了解蘑菇网品牌故事、用户口碑与联系方式。"
        keywords="蘑菇网,蘑菇网品牌,蘑菇网联系方式,蘑菇网口碑,蘑菇网品牌故事,时尚女装平台"
        canonicalUrl="https://www.moguwang.com/about"
        schema={[orgSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Story */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-10 items-center">
                <div className="lg:w-1/2">
                  <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">{aboutInfo.story.title}</h1>
                  <p className="text-sm md:text-base text-primary-500 font-medium mb-6">{aboutInfo.story.subtitle}</p>
                  <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                    {aboutInfo.story.content.split('\n\n').map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </div>
                </div>
                <div className="lg:w-1/2 w-full">
                  <img
                    src={aboutInfo.story.image}
                    alt="蘑菇网品牌故事"
                    className="w-full rounded-lg object-cover aspect-[4/3]"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">品牌历程</h2>
              <div className="relative">
                <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
                <div className="flex flex-col gap-6">
                  {aboutInfo.milestones.map((milestone, i) => (
                    <div key={milestone.year} className={`flex flex-col md:flex-row gap-4 md:gap-8 items-start ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} w-full`}>
                        <div className="bg-background-50 rounded-lg p-5">
                          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-2">{milestone.year}</span>
                          <p className="text-sm text-foreground-700">{milestone.event}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex w-4 h-4 rounded-full bg-accent-500 flex-shrink-0 mt-6 relative z-10"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">我们的价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.values.map((value) => (
                  <div key={value.title} className="text-center p-6 rounded-lg bg-background-100">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {aboutInfo.testimonials.map((testimonial) => (
                  <div key={testimonial.name} className="bg-background-50 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{testimonial.name}</p>
                        <p className="text-xs text-foreground-400">{testimonial.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{testimonial.content}</p>
                    <div className="flex items-center gap-0.5 mt-3 text-accent-500">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs"></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">常见问题</h2>
              <div className="flex flex-col gap-4">
                {aboutInfo.faqs.map((faq, i) => (
                  <details key={i} className="group rounded-lg bg-background-100 p-5 cursor-pointer">
                    <summary className="flex items-center justify-between text-sm font-semibold text-foreground-900 list-none">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">联系我们</h2>
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="lg:w-1/3 flex flex-col gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">客服热线</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">公司地址</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">工作时间</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.workingHours}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</span>
                    <span className="text-sm text-foreground-500">{aboutInfo.contact.weibo}</span>
                  </div>
                </div>

                <div className="lg:w-2/3">
                  <form onSubmit={handleContactSubmit} className="bg-background-50 rounded-lg p-6 md:p-8" data-readdy-form>
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        id="contact-subject"
                        type="text"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div className="mb-5">
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 *</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
                        placeholder="请输入您的留言内容（不超过500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                    >
                      发送留言
                    </button>
                    {formStatus !== 'idle' && (
                      <p className={`mt-3 text-sm ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                        {formMsg}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}